<template>
  <div class="instructor-signup">
    <div class="signup-container">
      <div class="signup-header">
        <h1>👨‍🏫 講師アカウント登録</h1>
        <p>オトエイゴ講師用のアカウントを作成します</p>
      </div>

      <form @submit.prevent="handleSignup" class="signup-form">
        <div class="form-group">
          <label for="email">メールアドレス</label>
          <input
            id="email"
            v-model="signupForm.email"
            type="email"
            required
            placeholder="instructor@example.com"
            class="form-input"
            :disabled="isLoading"
          />
        </div>

        <div class="form-group">
          <label for="fullName">お名前</label>
          <input
            id="fullName"
            v-model="signupForm.full_name"
            type="text"
            required
            placeholder="山田 太郎"
            class="form-input"
            :disabled="isLoading"
          />
        </div>

        <div class="form-group">
          <label for="password">パスワード</label>
          <input
            id="password"
            v-model="signupForm.password"
            type="password"
            required
            placeholder="8文字以上のパスワード"
            class="form-input"
            minlength="8"
            :disabled="isLoading"
          />
        </div>

        <div class="form-group">
          <label for="confirmPassword">パスワード確認</label>
          <input
            id="confirmPassword"
            v-model="signupForm.confirmPassword"
            type="password"
            required
            placeholder="パスワードを再入力"
            class="form-input"
            minlength="8"
            :disabled="isLoading"
          />
        </div>

        <button 
          type="submit" 
          :disabled="!canSignup || isLoading" 
          class="btn btn-primary"
        >
          {{ isLoading ? '登録中...' : '講師アカウントを作成' }}
        </button>
      </form>

      <!-- エラーメッセージ -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <!-- 成功メッセージ -->
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <!-- ログインリンク -->
      <div class="auth-links">
        <p>既にアカウントをお持ちですか？</p>
        <router-link to="/instructor/login" class="link">
          講師ログインはこちら
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../../supabaseClient'

const router = useRouter()

const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

// サインアップフォーム
const signupForm = ref({
  email: '',
  full_name: '',
  password: '',
  confirmPassword: ''
})

// バリデーション
const canSignup = computed(() => {
  return signupForm.value.email &&
         signupForm.value.full_name &&
         signupForm.value.password &&
         signupForm.value.confirmPassword &&
         signupForm.value.password === signupForm.value.confirmPassword &&
         signupForm.value.password.length >= 8
})

// 講師サインアップ処理
const handleSignup = async () => {
  if (!canSignup.value) return
  
  // パスワード確認
  if (signupForm.value.password !== signupForm.value.confirmPassword) {
    errorMessage.value = 'パスワードが一致しません'
    return
  }
  
  isLoading.value = true
  errorMessage.value = ''
  successMessage.value = ''
  
  try {
    // 1. Supabase Authでユーザー作成
    // user_metadataにapp_roleとfull_nameを保存（ログイン時にRPCで使用）
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: signupForm.value.email,
      password: signupForm.value.password,
      options: {
        emailRedirectTo: `${window.location.origin}/instructor/home`,
        data: {
          app_role: 'instructor',  // ログイン時のRPCで読み取る
          full_name: signupForm.value.full_name
        }
      }
    })
    
    if (authError) throw authError
    
    if (!authData.user) {
      throw new Error('ユーザー作成に失敗しました')
    }
    
    console.log('✅ 講師アカウント作成完了（初回ログイン時にプロフィール作成）')
    
    // メール確認が必要な場合のメッセージ
    if (authData.session === null) {
      successMessage.value = '講師アカウントが作成されました！\n確認メールをご確認の上、ログインしてください。'
    } else {
      successMessage.value = '講師アカウントが作成されました！'
    }
    
    // 3秒後に講師ログインページにリダイレクト
    setTimeout(() => {
      router.push('/instructor/login')
    }, 3000)
    
  } catch (error) {
    console.error('講師サインアップエラー:', error)
    
    // エラーメッセージの詳細化
    if (error.message?.includes('already registered')) {
      errorMessage.value = 'このメールアドレスは既に登録されています'
    } else if (error.message?.includes('weak password')) {
      errorMessage.value = 'パスワードが弱すぎます。8文字以上で設定してください'
    } else if (error.message?.includes('invalid email')) {
      errorMessage.value = '有効なメールアドレスを入力してください'
    } else {
      errorMessage.value = error.message || '登録に失敗しました'
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.instructor-signup {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 1rem;
}

.signup-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.signup-header {
  text-align: center;
  margin-bottom: 2rem;
}

.signup-header h1 {
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
}

.signup-header p {
  color: #6b7280;
  margin: 0;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-input:disabled {
  background: #f9fafb;
  color: #6b7280;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #fecaca;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
}

.auth-links {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.auth-links p {
  color: #6b7280;
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
}

.link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.link:hover {
  text-decoration: underline;
}

/* モバイル対応 */
@media (max-width: 768px) {
  .signup-container {
    padding: 1.5rem;
    margin: 0.5rem;
  }
  
  .signup-header h1 {
    font-size: 1.5rem;
  }
}
</style>
